import '../../Components2/common/rounded-inputs.css';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
  Checkbox,
  Chip,
  Typography,
} from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

const MAX_VISIBLE_CHIPS = 2;

export default function AutocompleteAsync({
  queryFn,
  queryKey,
  getOptionLabel,
  onChange,
  label = 'Seleccionar',
  initialValue = [],
  multiple = false,
}) {
  const [inputValue, setInputValue] = useState('');
  const [value, setValue] = useState(initialValue);
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingAll, setLoadingAll] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading, isFetching, isSuccess } = useQuery({
    queryKey: [...queryKey, { page, search: inputValue }],
    queryFn: () => queryFn({ page, search: inputValue }),
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (!isSuccess) return;
    const newOptions = data.centers;
    const more = data.pagination?.hasMore ?? false;
    setOptions((prev) => (page === 1 ? newOptions : [...prev, ...newOptions]));
    setHasMore(more);
  }, [data, isSuccess, page]);

  useEffect(() => {
    setPage(1);
  }, [inputValue]);

  const handleScroll = (event) => {
    const el = event.currentTarget;
    if (!isFetching && hasMore && el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
      setPage((prev) => prev + 1);
    }
  };

  const handleSelectAll = async (e) => {
    e.stopPropagation();
    setLoadingAll(true);
    let allCenters = [];
    let currentPage = 1;
    let keepFetching = true;
    while (keepFetching) {
      try {
        const res = await queryFn({ page: currentPage, search: inputValue });
        allCenters = [...allCenters, ...(res.centers || [])];
        keepFetching = res.pagination?.hasMore;
        currentPage++;
      } catch (err) {
        keepFetching = false;
      }
    }
    setOptions(allCenters);
    setValue(allCenters);
    onChange?.(allCenters);
    setLoadingAll(false);
  };

  return (
    <Autocomplete
      multiple={multiple}
      value={value}
      onChange={(_, newValue) => {
        setValue(newValue);
        onChange?.(newValue);
      }}
      options={options}
      loading={isFetching || isLoading || loadingAll}
      filterOptions={(x) => x}
      onInputChange={(_, val) => setInputValue(val)}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(a, b) => a?._id === b?._id}
      disableCloseOnSelect={multiple}
      renderTags={(selected, getTagProps) => {
        const visible = expanded ? selected : selected.slice(0, MAX_VISIBLE_CHIPS);
        const hiddenCount = selected.length - MAX_VISIBLE_CHIPS;
        return (
          <>
            {visible.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option._id}
                label={getOptionLabel(option)}
                size="small"
                sx={{ maxWidth: 150 }}
              />
            ))}
            {!expanded && hiddenCount > 0 && (
              <Chip
                label={`+${hiddenCount} más`}
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(true);
                }}
                sx={{ cursor: 'pointer' }}
              />
            )}
            {expanded && selected.length > MAX_VISIBLE_CHIPS && (
              <Chip
                label="Ver menos"
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(false);
                }}
                sx={{ cursor: 'pointer' }}
              />
            )}
          </>
        );
      }}
      renderOption={(props, option, { selected }) => (
        <li {...props} style={{ padding: '4px 8px' }}>
          <Checkbox
            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
            checkedIcon={<CheckBoxIcon fontSize="small" />}
            checked={selected}
            sx={{
              mr: 1,
              p: 0.5,
              color: 'black',
              '&.Mui-checked': {
                color: 'black',
              },
              '& svg': {
                color: 'black',
              },
            }}
          />
          <Typography variant="body2">{getOptionLabel(option)}</Typography>
        </li>
      )}
      ListboxProps={{
        onScroll: handleScroll,
        style: { maxHeight: 300, overflow: 'auto' },
      }}
      renderInput={(params) => {
        const showSelectAll = multiple && options.length > 0 && value.length < options.length;
        const showClearAll = multiple && value.length > 0;
        return (
          <TextField
            {...params}
            label={label}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {(isFetching || isLoading) && <CircularProgress size={20} />}
                  {showSelectAll && (
                    <Tooltip title={loadingAll ? 'Cargando todos...' : 'Seleccionar todos'}>
                      <span>
                        <IconButton size="small" onClick={handleSelectAll} tabIndex={-1} disabled={loadingAll}>
                          {loadingAll ? <CircularProgress size={18} /> : <DoneAllIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {showClearAll && (
                    <Tooltip title="Quitar todos">
                      <span>
                        <IconButton
                          size="small"
                          tabIndex={-1}
                          onClick={(e) => {
                            e.stopPropagation();
                            setValue([]);
                            onChange?.([]);
                          }}
                        >
                          <ClearAllIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'black',
                '& fieldset': {
                  borderColor: 'black',
                },
                '&:hover fieldset': {
                  borderColor: 'black',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'black',
                },
                '& input': {
                  color: 'black',
                },
              },
              '& label': {
                color: 'black',
              },
              '& label.Mui-focused': {
                color: 'black',
              },
            }}
          />
        );
      }}
    />
  );
}
